import React, { useState } from 'react';
import { X, Copy, Download, Check, Code, FileText, Braces } from 'lucide-react';
import type { DocumentAST, BlockNode } from '../types';
import { toKebabCase } from '../utils/styleResolver';

export interface CodeExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  doc: DocumentAST;
}

function compileNodeHTML(node: BlockNode, indent = '  '): string {
  const tag = node.type.toLowerCase().includes('section')
    ? 'section'
    : node.type.toLowerCase().includes('heading')
    ? 'h2'
    : node.type.toLowerCase().includes('paragraph')
    ? 'p'
    : node.type.toLowerCase().includes('button')
    ? 'button'
    : 'div';

  const idAttr = node.htmlId ? ` id="${node.htmlId}"` : ` id="wb-${node.id.slice(0, 8)}"`;
  const classList = [`wb-${node.type.toLowerCase()}`, ...(node.classes || [])].join(' ');
  const classAttr = classList ? ` class="${classList}"` : '';

  let inner = '';
  if (node.props?.headline) {
    inner += `\n${indent}  <h2>${String(node.props.headline)}</h2>`;
  }
  if (node.props?.subheadline) {
    inner += `\n${indent}  <p>${String(node.props.subheadline)}</p>`;
  }
  if (node.props?.text) {
    inner += `\n${indent}  <span>${String(node.props.text)}</span>`;
  }

  if (node.children && node.children.length > 0) {
    inner +=
      '\n' +
      node.children.map((child) => compileNodeHTML(child, indent + '  ')).join('\n') +
      `\n${indent}`;
  }

  return `${indent}<${tag}${idAttr}${classAttr}>${inner}</${tag}>`;
}

function compileNodeCSS(root: BlockNode): string {
  const baseRules: string[] = [];
  const hoverRules: string[] = [];
  const tabletRules: string[] = [];
  const mobileRules: string[] = [];

  function traverse(node: BlockNode) {
    const sel = node.htmlId ? `#${node.htmlId}` : `#wb-${node.id.slice(0, 8)}`;

    if (node.styles?.base && Object.keys(node.styles.base).length > 0) {
      const decls = Object.entries(node.styles.base)
        .map(([k, v]) => `  ${toKebabCase(k)}: ${v};`)
        .join('\n');
      baseRules.push(`${sel} {\n${decls}\n}`);
    }

    if (node.styles?.hover && Object.keys(node.styles.hover).length > 0) {
      const decls = Object.entries(node.styles.hover)
        .map(([k, v]) => `  ${toKebabCase(k)}: ${v};`)
        .join('\n');
      hoverRules.push(`${sel}:hover {\n${decls}\n}`);
    }

    const tabletStyles = node.styles?.tablet || node.styles?.md;
    if (tabletStyles && Object.keys(tabletStyles).length > 0) {
      const decls = Object.entries(tabletStyles)
        .map(([k, v]) => `  ${toKebabCase(k)}: ${v};`)
        .join('\n');
      tabletRules.push(`  ${sel} {\n  ${decls}\n  }`);
    }

    if (node.styles?.mobile && Object.keys(node.styles.mobile).length > 0) {
      const decls = Object.entries(node.styles.mobile)
        .map(([k, v]) => `  ${toKebabCase(k)}: ${v};`)
        .join('\n');
      mobileRules.push(`  ${sel} {\n  ${decls}\n  }`);
    }

    if (node.children) {
      for (const child of node.children) {
        traverse(child);
      }
    }
  }

  traverse(root);

  let output = '/* Base Styles (Desktop-First) */\n' + baseRules.join('\n\n');
  if (hoverRules.length > 0) {
    output += '\n\n/* Hover States */\n' + hoverRules.join('\n\n');
  }
  if (tabletRules.length > 0) {
    output += `\n\n/* Tablet Viewport (≤992px) */\n@media (max-width: 992px) {\n${tabletRules.join('\n\n')}\n}`;
  }
  if (mobileRules.length > 0) {
    output += `\n\n/* Mobile Viewport (≤767px) */\n@media (max-width: 767px) {\n${mobileRules.join('\n\n')}\n}`;
  }

  return output;
}

export function CodeExportModal({
  isOpen,
  onClose,
  doc,
}: CodeExportModalProps): React.JSX.Element | null {
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'json'>('html');
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const htmlCode = `<!DOCTYPE html>\n<html lang="en">\n<head>\n  <meta charset="UTF-8">\n  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n  <title>${doc.seo?.title || 'Published Page'}</title>\n  <link rel="stylesheet" href="styles.css">\n</head>\n<body>\n${compileNodeHTML(doc.root, '  ')}\n</body>\n</html>`;
  const cssCode = compileNodeCSS(doc.root);
  const jsonCode = JSON.stringify(doc, null, 2);

  const activeContent = activeTab === 'html' ? htmlCode : activeTab === 'css' ? cssCode : jsonCode;

  const handleCopy = () => {
    navigator.clipboard.writeText(activeContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([activeContent], {
      type: activeTab === 'json' ? 'application/json' : 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = activeTab === 'html' ? 'index.html' : activeTab === 'css' ? 'styles.css' : 'document-ast.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden text-foreground">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4 text-[#3b97e3]" />
            <span className="font-bold text-sm">GrapesJS Code Viewer & Exporter</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Tab switchers */}
            <div className="flex bg-muted/60 p-0.5 rounded border border-border gap-0.5">
              <button
                type="button"
                onClick={() => setActiveTab('html')}
                className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded transition-colors cursor-pointer ${
                  activeTab === 'html' ? 'bg-background text-[#3b97e3] font-bold shadow-xs' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                <span>HTML</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('css')}
                className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded transition-colors cursor-pointer ${
                  activeTab === 'css' ? 'bg-background text-[#3b97e3] font-bold shadow-xs' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Code className="h-3.5 w-3.5" />
                <span>CSS</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('json')}
                className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded transition-colors cursor-pointer ${
                  activeTab === 'json' ? 'bg-background text-[#3b97e3] font-bold shadow-xs' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Braces className="h-3.5 w-3.5" />
                <span>AST JSON</span>
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Code Content Box */}
        <div className="flex-1 bg-slate-950 text-slate-100 p-4 font-mono text-xs overflow-auto select-text leading-relaxed">
          <pre>{activeContent}</pre>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
          <span className="text-xs text-muted-foreground">
            {activeContent.split('\n').length} lines · {activeContent.length} bytes
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted text-xs font-semibold flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
              <span>{copied ? 'Copied!' : 'Copy to Clipboard'}</span>
            </button>

            <button
              type="button"
              onClick={handleDownload}
              className="px-3.5 py-1.5 rounded-lg bg-[#3b97e3] text-white hover:opacity-90 text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Download File</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
